import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Music, ShoppingCart, Package, Tag, Truck, Sparkles, ArrowRight } from "lucide-react";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { useAuth, useCart } from "../App";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BundlesPage = () => {
  const { user } = useAuth();
  const { addToCart } = useCart();
  const [bundles, setBundles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    const fetchBundles = async () => {
      try {
        const res = await axios.get(`${API}/bundles/`);
        setBundles(res.data);
      } catch (e) {
        console.error("Failed to fetch bundles");
      } finally {
        setLoading(false);
      }
    };
    fetchBundles();
  }, []);

  const handleAddBundle = async (bundle) => {
    if (!user) {
      toast.error("Please login to add items to cart");
      return;
    }
    if (bundle.bundle_type === "merch") {
      for (const item of (bundle.items || [])) {
        await addToCart({
          item_type: "merch",
          item_id: item.merch_id,
          size: item.variants?.[0]?.size,
          color: item.variants?.[0]?.color,
          quantity: 1
        });
      }
    } else {
      for (const beat of (bundle.beats || bundle.items || [])) {
        await addToCart({
          item_type: "beat",
          item_id: beat.beat_id,
          license_type: "basic"
        });
      }
    }
    toast.success(`Bundle "${bundle.name}" added to cart!`);
  };

  const filteredBundles = bundles.filter(b => {
    if (filter === "all") return true;
    if (filter === "merch") return b.bundle_type === "merch";
    if (filter === "beats") return b.bundle_type !== "merch";
    return true;
  });

  const merchBundles = bundles.filter(b => b.bundle_type === "merch");
  const beatBundles = bundles.filter(b => b.bundle_type !== "merch");

  return (
    <div className="min-h-screen py-10 px-4 sm:px-6" data-testid="bundles-page">
      <div className="max-w-7xl mx-auto">
        <Badge className="bg-neon-cyan/20 text-neon-cyan border-neon-cyan/50 mb-4">
          Bundle Deals
        </Badge>
        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tight mb-3">
          Save <span className="text-neon-magenta">15%</span> with Bundles
        </h1>
        <p className="text-zinc-400 text-sm sm:text-base max-w-xl mb-8">
          Curated collections at discounted prices. More gear, less money.
        </p>

        {/* Filter */}
        <div className="flex gap-2 mb-8">
          {["all", "merch", "beats"].map(f => (
            <Button
              key={f}
              variant={filter === f ? "default" : "outline"}
              size="sm"
              onClick={() => setFilter(f)}
              className={filter === f ? "bg-neon-cyan text-black" : "border-white/20"}
            >
              {f === "all" ? "All Bundles" : f === "merch" ? `Merch (${merchBundles.length})` : `Beats (${beatBundles.length})`}
            </Button>
          ))}
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-80 bg-zinc-900/50 animate-pulse rounded-xl" />
            ))}
          </div>
        ) : filteredBundles.length === 0 ? (
          <div className="text-center py-20">
            <Package className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
            <p className="text-zinc-400">No bundles available yet</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredBundles.map((bundle, idx) => (
              <motion.div
                key={bundle.bundle_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
                className="glass border border-white/10 rounded-xl overflow-hidden group hover:border-white/20 transition-all"
                data-testid={`bundle-card-${bundle.bundle_id}`}
              >
                {/* Bundle Image */}
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={bundle.image_url || ""}
                    alt={bundle.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />

                  {/* Savings Badge */}
                  <Badge className="absolute top-3 right-3 bg-red-500 text-white border-0 font-bold text-sm" data-testid={`savings-badge-${bundle.bundle_id}`}>
                    <Tag className="w-3 h-3 mr-1" />
                    Save ${bundle.savings?.toFixed(2)}
                  </Badge>

                  {bundle.bundle_type === "merch" && (
                    <Badge className="absolute top-3 left-3 bg-neon-cyan/20 text-neon-cyan border-neon-cyan/30 text-[10px]">
                      <Truck className="w-3 h-3 mr-1" /> Ships Worldwide
                    </Badge>
                  )}

                  {/* Discount percent */}
                  <div className="absolute bottom-3 left-3">
                    <span className="text-3xl font-heading font-bold text-neon-magenta">
                      {bundle.discount_percent || 15}% OFF
                    </span>
                  </div>
                </div>

                {/* Bundle Info */}
                <div className="p-5">
                  <h3 className="font-heading text-lg uppercase tracking-wide mb-1">{bundle.name}</h3>
                  <p className="text-zinc-400 text-xs mb-4 line-clamp-2">{bundle.description}</p>

                  {/* Items Preview */}
                  <div className="mb-4">
                    <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-2">
                      {(bundle.items || []).length} items included
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {(bundle.items || []).slice(0, 5).map((item, i) => (
                        <Badge key={i} variant="outline" className="border-white/10 text-zinc-400 text-[10px]">
                          {item.name || item.title}
                        </Badge>
                      ))}
                      {(bundle.items || []).length > 5 && (
                        <Badge variant="outline" className="border-white/10 text-zinc-400 text-[10px]">
                          +{(bundle.items || []).length - 5} more
                        </Badge>
                      )}
                    </div>
                  </div>

                  {/* Price */}
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-2xl font-mono text-neon-magenta font-bold">
                        ${bundle.price?.toFixed(2)}
                      </span>
                      <span className="text-sm text-zinc-500 line-through ml-2">
                        ${bundle.original_price?.toFixed(2)}
                      </span>
                    </div>
                    <Button
                      onClick={() => handleAddBundle(bundle)}
                      className="bg-neon-magenta text-black font-bold text-xs uppercase"
                      data-testid={`add-bundle-${bundle.bundle_id}`}
                    >
                      <ShoppingCart className="w-4 h-4 mr-1" />
                      Add All
                    </Button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BundlesPage;
